import { Injectable } from '@angular/core';
import { WinnerApiService } from '@fantaz/shared/data-access/api';
import { Winner } from '@fantaz/shared/data-access/models';
import { ToastService } from '@fantaz/shared/utils/toast';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, switchMap, throwError, withLatestFrom } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { ProfileState } from '../profile/profile.reducer';
import * as ProfileSelectors from '../profile/profile.selectors';
import * as WinnerActions from './winner.actions';
import { WinnerState } from './winner.reducer';
import * as WinnerSelectors from './winner.selectors';

@Injectable()
export class WinnerEffects {
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WinnerActions.load),
      mergeMap((action) => {
        return this.winnerApiService
          .fetchWinner({
            eventInstanceId: action.eventInstanceId,
            playInstanceId: action.playInstanceId,
          })
          .pipe(
            switchMap((result) => {
              if (result.success) {
                return [
                  WinnerActions.loadWinnerSuccess({
                    winner: result.payload as Winner,
                  }),
                ];
              } else {
                // in case of error 401 then redirect to personal info
                if (result.error?.code === 401 || result.error?.code === 400) {
                  return [
                    WinnerActions.loadWinnerFailure({
                      error:
                        'It seems like this link is not valid anymore, or the account you are logged in is not the winner of this tournament. Please log in with the account you used to win the tournament.',
                    }),
                  ];
                }
                return [
                  WinnerActions.loadWinnerFailure({
                    error: result.error?.message,
                  }),
                ];
              }
            }),
            catchError<any, any>((error: any) => {
              console.error('Error', error);
              return WinnerActions.loadWinnerFailure({ error });
            }),
          );
      }),
      catchError((err: any) => {
        console.error('Error', err);
        return of(WinnerActions.loadWinnerFailure({ error: err }));
      }),
    ),
  );

  confirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WinnerActions.confirm),
      withLatestFrom(
        this.winnerStore.select(WinnerSelectors.getParams),
        this.profileStore.select(ProfileSelectors.getProfile),
        (action, params, profile) => [action, { params, profile }],
      ),
      mergeMap(([action, state]: any[]) => {
        const { params, profile } = state;
        return this.winnerApiService
          .confirmAddress({
            eventInstanceId: params?.eventInstanceId as string,
            playInstanceId: params?.playInstanceId as string,
            profile,
          })
          .pipe(
            switchMap((result) => {
              if (result.success) {
                // notify user
                this.toastService.openSnackBar(
                  'Your address has been confirmed',
                );

                return [
                  // TODO: Return in service winner data, for not load more
                  WinnerActions.confirmWinnerSuccess(),
                  WinnerActions.load({ ...params, confirmedNow: true }),
                ];
              } else {
                this.toastService.openSnackBar('Error confirming your address');
                return [
                  WinnerActions.confirmWinnerFailure({ error: result.error }),
                ];
              }
            }),
            catchError<any, any>((error: any) => {
              return throwError(() => error);
            }),
          );
      }),
      catchError((error) => {
        console.error('CLAIM PRIZE ERROR:', error);
        return of(
          WinnerActions.confirmWinnerFailure({
            error:
              'An unknown error occurred while confirming the shipping address, please refresh your browser. If the problem persists, please contact customer support.',
          }),
        );
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly winnerStore: Store<WinnerState>,
    private readonly profileStore: Store<ProfileState>,
    private readonly toastService: ToastService,
    private readonly winnerApiService: WinnerApiService,
  ) {}
}
